import React, { useState, useRef, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';
import documentConfigs from '../../utils/documentConfigs.json';

// Import images
import edaplogo from "../../img/edaplogo.png";
import crlogo from "../../../img/crlogo.png";
import undlogo from "../../img/undlogo.png";
import './UNDDiploma.css';

const UNDMasterTemplate = ({ app }) => {
    const [formData, setFormData] = useState({
        noms: '',
        dateNaissance: '',
        lieuNaissance: '',
        province: '',
        pays: '',
        department: '',
        specialite: '',
        mention: 'Très Bien',
        anneeAcademique: '',
        numeroDoc: '',
        dateEmission: new Date().toISOString().split('T')[0]
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `und-master/UNCR/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;
            
            let qrData = `UND MASTER - UNCR\n`;
            Object.keys(selectedDocument).forEach(key => {
                if (key !== 'createdAt' && selectedDocument[key]) {
                    qrData += `${key}: ${selectedDocument[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: UND - Université Nationale de la Décentralisation`;
            
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate required fields
        const requiredFields = ['noms', 'dateNaissance', 'lieuNaissance', 'pays', 'department', 'specialite', 'mention', 'anneeAcademique', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }

        // Save to Firebase
        const documentRefPath = isEditing 
            ? ref(db, `und-master/UNCR/${editingId}`)
            : ref(db, `und-master/UNCR/${formData.noms.replace(/\s+/g, '_')}_${Date.now()}`);
        
        const dataToSave = isEditing 
            ? { ...formData }
            : { ...formData, createdAt: new Date().toISOString() };
        
        set(documentRefPath, dataToSave)
            .then(() => {
                console.log(isEditing ? 'UND master updated successfully' : 'UND master saved successfully');
                message.success(isEditing ? 'Document modifié avec succès' : 'Document créé avec succès');
                resetForm();
            })
            .catch((error) => {
                console.error('Error saving UND master:', error);
                message.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de la création');
            });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const resetForm = () => {
        setFormData({
            noms: '',
            dateNaissance: '',
            lieuNaissance: '',
            province: '',
            pays: '',
            department: 'Sciences Juridiques',
            specialite: 'Droit des Affaires et Fiscalité',
            mention: 'Très Bien',
            anneeAcademique: '',
            numeroDoc: '',
            dateEmission: new Date().toISOString().split('T')[0]
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            noms: document.noms || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            province: document.province || '',
            pays: document.pays || '',
            department: document.department || 'Sciences Juridiques',
            specialite: document.specialite || 'Droit des Affaires et Fiscalité',
            mention: document.mention || 'Très Bien',
            anneeAcademique: document.anneeAcademique || '',
            numeroDoc: document.numeroDoc || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0]
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `und-master/UNCR/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('und-master-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        const clone = element.cloneNode(true);
        clone.style.cssText = `
            width: 29.7cm !important;
            height: 21cm !important;
            margin: 0 auto !important;
            padding: 2cm !important;
            background: white !important;
            position: relative !important;
            box-sizing: border-box !important;
            font-family: 'Times New Roman', serif !important;
            display: flex !important;
            flex-direction: column !important;
        `;

        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = `
            position: fixed !important;
            top: -9999px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            z-index: -1 !important;
            width: 29.7cm !important;
            background: white !important;
        `;
        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        try {
            // Wait for images to load
            const images = clone.querySelectorAll('img');
            await Promise.all(Array.from(images).map(img => {
                return new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                    } else {
                        img.onload = resolve;
                        img.onerror = resolve;
                    }
                });
            }));

            // Small delay to ensure rendering is complete
            await new Promise(resolve => setTimeout(resolve, 500));

            // A4 landscape dimensions: 29.7cm x 21cm = 842pt x 595pt
            // At 96 DPI: 29.7cm = 1123px, 21cm = 794px
            // Double the scale for better quality
            const scale = 4;
            const A4_LANDSCAPE_WIDTH_PX = 1123;
            const A4_LANDSCAPE_HEIGHT_PX = 794;

            const canvas = await html2canvas(clone, {
                scale: scale,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#FFFFFF',
                width: A4_LANDSCAPE_WIDTH_PX,
                height: A4_LANDSCAPE_HEIGHT_PX,
                logging: false,
                imageTimeout: 10000,
                windowWidth: A4_LANDSCAPE_WIDTH_PX,
                windowHeight: A4_LANDSCAPE_HEIGHT_PX
            });
            
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('l', 'pt', 'a4'); // 'l' for landscape
            const pageWidth = pdf.internal.pageSize.getWidth(); // 842pt
            const pageHeight = pdf.internal.pageSize.getHeight(); // 595pt

            // Scale image to fill page with left margin
            const imgWidth = canvas.width / scale;
            const imgHeight = canvas.height / scale;
            const widthRatio = pageWidth / imgWidth;
            const heightRatio = pageHeight / imgHeight;
            // Slightly reduce vertical scale to prevent cutting signatures
            const ratio = Math.min(widthRatio, heightRatio * 0.95) * 1.2;
            
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            const xOffset = 20; // Left margin in points
            const yOffset = 20; // Top margin in points

            pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidth, scaledHeight);
            pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_UND_master.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        } finally {
            document.body.removeChild(tempContainer);
        }
    };

    return (
        <div className="und-diploma-container">
            <div className="und-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo UND" className="und-form-logo" />
                    <h2>Formulaire de Master UND</h2>
                </div>
                
                <form onSubmit={handleSubmit} className="und-form">
                    <div className="und-form-row">
                        <input
                            type="text"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder="Nom complet"
                            required
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="und-form-row">
                        <input
                            type="text"
                            name="lieuNaissance"
                            value={formData.lieuNaissance}
                            onChange={handleInputChange}
                            placeholder="Lieu de naissance"
                            required
                        />
                        <input
                            type="text"
                            name="province"
                            value={formData.province}
                            onChange={handleInputChange}
                            placeholder="Province (optionnel)"
                        />
                    </div>
                    
                    <div className="und-form-row">
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                        />
                        <input
                            type="text"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="Département (ex: Sciences Juridiques)"
                            required
                        />
                    </div>
                    
                    <div className="und-form-row">
                        <input
                            type="text"
                            name="specialite"
                            value={formData.specialite}
                            onChange={handleInputChange}
                            placeholder="Spécialité"
                            required
                        />
                        <select
                            name="mention"
                            value={formData.mention}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="Excellent">Excellent</option>
                            <option value="Très Bien">Très Bien</option>
                            <option value="Bien">Bien</option>
                            <option value="Assez Bien">Assez Bien</option>
                            <option value="Passable">Passable</option>
                        </select>
                    </div>
                    
                    <div className="und-form-row">
                        <input
                            type="text"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder="Année académique (Ex: 2024-2025)"
                            required
                        />
                        <input
                            type="text"
                            name="numeroDoc"
                            value={formData.numeroDoc}
                            onChange={handleInputChange}
                            placeholder="Numéro du document (optionnel)"
                        />
                    </div>
                    
                    <div className="und-form-row">
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <button type="submit" className="und-submit-btn">
                        {isEditing ? 'Modifier le Master UND' : 'Créer le Master UND'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="und-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="und-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="und-document-item">
                            <button
                                className="und-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - UND Master
                            </button>
                            <button
                                className="und-document-toggle"
                                onClick={() => handleEditDocument(document)}
                                style={{ 
                                    marginRight: '8px', 
                                    background: '#ffc107',
                                    color: '#000'
                                }}
                            >
                                <Edit size={16} style={{ marginRight: '8px' }} />
                                Modifier
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="und-delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Master UND</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="und-modal-content">
                            <UNDMasterDocument document={selectedDocument} qrCodeData={qrCodeData} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const UNDMasterDocument = ({ document: docData, qrCodeData }) => {
    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    return (
        <div id="und-master-document" className="und-diploma-document">
            {/* Header */}
            <div className="und-header">
                <div className="und-header-row">
                    <div className="und-left-logo">
                        <img src={undlogo} alt="Logo Left" className="und-logo" />
                    </div>
                    <div className="und-header-center">
                        <h1 className="und-country">REPUBLIQUE DEMOCRATIQUE DU CONGO</h1>
                        <h2 className="und-university">UNIVERSITE NATIONALE DE LA DECENTRALISATION</h2>
                        <p className="und-address" style={{ marginTop: '8px', fontSize: '12px' }}>Avenue Marechal No 27 ; Quartier 7; Commune Ndjili;  Ville de Kinshasa</p>
                        <p className="und-contact" style={{ fontSize: '12px' }}>Web: www.und.ac.cd ; Web: info@und.ad.cd</p>
                    </div>
                    <div className="und-right-logo">
                        <img src={crlogo} alt="Logo Right" className="und-logo" />
                    </div>
                </div>
            </div>

            {/* Title Section */}
            <div className="und-title-section">
                <div className="und-title-row">
                    <div className="und-title-spacer"></div>
                    <h1 className="und-diploma-title">DIPLOME DE MASTER</h1>
                    <div className="und-number-box">
                        <span className="number-label">N°</span>
                        <span className="number-value">{docData.numeroDoc || ''}</span>
                    </div>
                </div>
            </div>

            {/* Document Body */}
            <div className="und-document-body">
                <div className="und-legal-references">
                    <p className="vu-clause">Vu la Loi-cadre n°14/004 du 11 février 2014 de l'Enseignement National;</p>
                    <p className="vu-clause">Vu le décret n° 22/39 du 8 décembre 2022 portant organisation et fonctionnement du système LMD en République Démocratique du Congo;</p>
                    <p className="vu-clause">Vu le décret n° 24/23. du 15 Mars 2024 relatif aux diplômes et grades académiques;</p>
                    <p className="vu-clause">Vu l'arrêté ministériel 401 du 28 août 2023 portant modalités d'évaluation, de progression et d'orientation dans les cycles d'études;</p>
                    <p className="vu-clause">Vu l'Arrêté Ministériel 0741/06/MINESU/CABMIN/SASM/MMK/2025 du 07 Juillet 2025 portant création et prise en charge par le Trésor Public de l'Université Nationale de la Décentralisation(UND) comme Etablissements de l'Enseignement Supérieur et Universitaire en RDC ;</p>
                    <p className="vu-clause">Vu l'Arrêté Ministériel 004 MINESU/CABMIN/SASM/MMK/2025 du 06 février 2025 portant organisation et fonctionnement de l'Enseignement ouvert et à distance au sein des Etablissements de l'Enseignement Supérieur et Universitaire en République Démocratique du Congo;</p>
                    <p className="vu-clause">Vu la collaboration entre l'UND et le Distant Production House University (DPHU) pour décerner des co-diplômes avec UND;</p>
                    <p className="vu-clause">Vu les procès-verbaux du Jury attestant que l'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires ;</p>
                </div>
                
                <div className="und-diploma-declaration">
                    <p className="main-declaration">
                        Le diplôme de Master en <strong>{docData.department}</strong> Spécialité: <strong>{docData.specialite}</strong> est délivré à <strong>{docData.noms}</strong> né le <strong>{formatDateWithMonthName(docData.dateNaissance)}</strong> à <strong>{docData.lieuNaissance}</strong>{docData.province ? ` Province du ${docData.province}` : ''}/<strong>{docData.pays}</strong> au titre de l'année académique <strong>{docData.anneeAcademique}</strong> avec la mention <strong>{docData.mention}</strong> et lui confère le grade de Master (Mphil), pour en jouir avec les droits et prérogatives qui y sont attachés.
                    </p>
                </div>

                <div className="und-emission-date">
                    <p>Fait à Kinshasa, le <strong>{formatDateWithMonthName(docData.dateEmission)}</strong></p>
                </div>
            </div>

            {/* Signatures */}
            <div className="und-signatures">
                <div className="und-signature-row">
                    <div className="und-left-signature">
                        <hr className="signature-line-separator" />
                        <p className="signature-title">Le Président du Conseil d'Administration</p>
                    </div>
                    <div className="und-right-signature">
                        <hr className="signature-line-separator" />
                        <p className="signature-title">Signature du titulaire</p>
                    </div>
                    {qrCodeData && (
                        <div className="und-qr-section">
                            <img
                                src={qrCodeData}
                                alt="QR Code - Document Verification"
                                className="qr-code"
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    border: '1px solid #000'
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UNDMasterTemplate;